import { all, call, put, select, take } from 'redux-saga/effects';
import { loadServices, clearServices } from 'actions/entities/serviceActions';
import {
  loadClientProfiles,
  loadDepartments,
  loadPositions,
  clearClientProfiles,
  clearDepartments,
  clearPositions,
  loadCompanies,
  loadUsersInitial,
} from 'actions/entities/userActions';
import { loadCredentialGroups, clearCredentialGroups } from 'actions/entities/credentialActions';
import {
  loadTaskLabels,
  loadTaskStatuses,
  clearTaskLabels,
  clearTaskStatuses,
  loadWorkflows,
} from 'actions/entities/taskActions';
import {
  loadArticleTags,
  loadArticleCategories,
  clearArticleTags,
  clearArticleCategories,
} from 'actions/entities/articleActions';
import { loadEventTypes, clearEventTypes } from 'actions/entities/eventActions';
import {
  LOAD_ARTICLE_CATEGORIES_SUCCESS,
  LOAD_ARTICLE_TAGS_SUCCESS,
  LOAD_CLIENT_PROFILES_SUCCESS,
  LOAD_CREDENTIAL_GROUPS_SUCCESS,
  LOAD_DEPARTMENTS_SUCCESS,
  LOAD_EVENT_TYPES_SUCCESS,
  LOAD_POSITIONS_SUCCESS,
  LOAD_SERVICES_SUCCESS,
  LOAD_TASK_LABELS_SUCCESS,
  LOAD_TASK_STATUSES_SUCCESS,
  LOAD_PROJECTS_SUCCESS,
  LOAD_COMPANIES_SUCCESS,
  LOAD_USERS_SUCCESS,
  LOAD_USER_PERMISSIONS_SUCCESS,
  LOAD_WORKFLOWS_SUCCESS,
} from 'constants/ActionTypes';
import { getUserPermissions } from 'selectors/entities/rbacSelectors';
import {
  getClientProfilesFromState,
  getDepartmentsFromState,
  getPositionsFromState,
} from 'selectors/entities/userSelectors';
import { getServicesFromState } from 'selectors/entities/servicesSelectors';
import { getCredentialGroupsFromState } from 'selectors/entities/credentialSelectors';
import { getTaskLabels, getTaskStatuses } from 'selectors/entities/taskSelectors';
import { getEventTypesFromState } from 'selectors/entities/eventSelectors';
import { getArticleCategoriesFromState, getArticleTagsFromState } from 'selectors/entities/articleSelectors';
import { loadProjects } from 'actions/entities/projectActions';
import { PROJECT, TASK_WORKFLOW, USER_COMPANY } from 'constants/RbacPermissions';
import { getEntitiesUpdated } from '../../selectors/entities/metaInformationSelectors';
import { LOAD_ENTITIES_UPDATED_SUCCESS } from '../../constants/ActionTypes';
import { loadEntitiesUpdated } from '../../actions/entities/metaInformationActions';
import { loadNotifications } from 'actions/ui/notificationDrawerActions';
import { loadUserPermissions } from 'actions/entities/rbacActions';
import { setCompanyLanguage } from 'helpers/companyHelpers';

const stateInformation = {
  taskLabels: {
    successActionType: LOAD_TASK_LABELS_SUCCESS,
    clearActionCreator: clearTaskLabels,
    loadActionCreator: loadTaskLabels,
    selector: getTaskLabels,
  },
  taskStatuses: {
    successActionType: LOAD_TASK_STATUSES_SUCCESS,
    clearActionCreator: clearTaskStatuses,
    loadActionCreator: loadTaskStatuses,
    selector: getTaskStatuses,
  },
  departments: {
    successActionType: LOAD_DEPARTMENTS_SUCCESS,
    clearActionCreator: clearDepartments,
    loadActionCreator: loadDepartments,
    selector: getDepartmentsFromState,
  },
  positions: {
    successActionType: LOAD_POSITIONS_SUCCESS,
    clearActionCreator: clearPositions,
    loadActionCreator: loadPositions,
    selector: getPositionsFromState,
  },
  articleCategories: {
    successActionType: LOAD_ARTICLE_CATEGORIES_SUCCESS,
    clearActionCreator: clearArticleCategories,
    loadActionCreator: loadArticleCategories,
    selector: getArticleCategoriesFromState,
  },
  articleTags: {
    successActionType: LOAD_ARTICLE_TAGS_SUCCESS,
    clearActionCreator: clearArticleTags,
    loadActionCreator: loadArticleTags,
    selector: getArticleTagsFromState,
  },
  services: {
    successActionType: LOAD_SERVICES_SUCCESS,
    clearActionCreator: clearServices,
    loadActionCreator: loadServices,
    selector: getServicesFromState,
  },
  eventTypes: {
    successActionType: LOAD_EVENT_TYPES_SUCCESS,
    clearActionCreator: clearEventTypes,
    loadActionCreator: loadEventTypes,
    selector: getEventTypesFromState,
  },
  credentialGroups: {
    successActionType: LOAD_CREDENTIAL_GROUPS_SUCCESS,
    clearActionCreator: clearCredentialGroups,
    loadActionCreator: loadCredentialGroups,
    selector: getCredentialGroupsFromState,
  },
  clientProfiles: {
    successActionType: LOAD_CLIENT_PROFILES_SUCCESS,
    clearActionCreator: clearClientProfiles,
    loadActionCreator: loadClientProfiles,
    selector: getClientProfilesFromState,
  },
};

const getReduceItemHelperByKey = (key) => {
  const value = stateInformation[key];
  if (!value) {
    return null;
  }

  return value;
};

let loadedEntitiesSuccessActinType = [];

export default function* loadEntitiesSaga(isInitialRequest = false) {
  const prevResponseEntitiesUpdated = yield select(getEntitiesUpdated);

  yield put(loadEntitiesUpdated());
  const action = yield take(LOAD_ENTITIES_UPDATED_SUCCESS);

  yield call(watcherLoadEntitiesSaga, isInitialRequest, { ...action.payload }, prevResponseEntitiesUpdated);
}

function* watcherLoadEntitiesSaga(isInitialRequest = false, entitiesUpdatedResponse, prevResponseEntitiesUpdated) {
  const isFirstRequest = prevResponseEntitiesUpdated === null;

  if (isInitialRequest) {
    yield put(loadUsersInitial());
    yield take(LOAD_USERS_SUCCESS);

    yield put(loadUserPermissions());
    yield take(LOAD_USER_PERMISSIONS_SUCCESS);

    const permissions = yield select(getUserPermissions);
    if (permissions.get(USER_COMPANY.view)) {
      yield put(
        loadCompanies((data) => {
          const company = data?.items[0] ?? {};
          const { language, alias } = company;
          setCompanyLanguage(language, alias);
        }),
      );
      loadedEntitiesSuccessActinType.push(LOAD_COMPANIES_SUCCESS);
    }

    // Не ожидаем загрузки т.к оно может выполниться асинхронно
    yield put(loadNotifications());

    if (permissions.get(TASK_WORKFLOW.available)) {
      yield put(loadWorkflows());
      loadedEntitiesSuccessActinType.push(LOAD_WORKFLOWS_SUCCESS);
    }

    if (permissions.get(PROJECT.index)) {
      yield put(
        loadProjects({
          fields: [
            'projectId',
            'userId',
            'workflowId',
            'name',
            'team',
            'status',
            'favoriteMap',
            'isGoogleSearchConnected',
            'site',
            'showTimeCost',
            'canUserUpdate',
            'canClientUpdate',
            'canUserCloseTask',
            'canClientCloseTask',
            'canUserChangeDate',
            'canClientChangeDate',
            'canUserChangeUser',
            'canClientChangeUser',
            'canUserChangeStatus',
            'canClientChangeStatus',
            'canUserChangeLabels',
            'canClientChangeLabels',
            'canUserChangePriority',
            'canClientChangePriority',
          ],
          extraFields: ['clientProfiles', 'services'],
          perPage: 0,
          page: null,
          filters: {
            status: {
              value: 'active',
            },
          },
        }),
      );
      loadedEntitiesSuccessActinType.push(LOAD_PROJECTS_SUCCESS);
    }
  }

  for (const key in entitiesUpdatedResponse) {
    const reduceItemHelper = getReduceItemHelperByKey(key);
    if (!reduceItemHelper) {
      continue;
    }

    if (isFirstRequest) {
      yield call(loadEntityAfterCheckUpdated, {
        successActionType: reduceItemHelper.successActionType,
        loadActionCreator: reduceItemHelper.loadActionCreator,
      });
      continue;
    }

    const data = yield select(reduceItemHelper.selector);
    const lastTimestamp = prevResponseEntitiesUpdated[key];
    const timestamp = entitiesUpdatedResponse[key];
    if ((lastTimestamp && !timestamp) || (!timestamp && !lastTimestamp && data.size)) {
      yield call(purchaseEntityAfterCheckUpdated, { clearActionCreator: reduceItemHelper.clearActionCreator });
      continue;
    }

    if (timestamp === null || (lastTimestamp === timestamp && data.size)) {
      continue;
    }

    yield call(loadEntityAfterCheckUpdated, {
      successActionType: reduceItemHelper.successActionType,
      loadActionCreator: reduceItemHelper.loadActionCreator,
    });
  }

  // waiting all entities
  yield all(loadedEntitiesSuccessActinType.map((type) => take(type)));

  loadedEntitiesSuccessActinType = [];
}

function* purchaseEntityAfterCheckUpdated({ clearActionCreator }) {
  yield put(clearActionCreator());
}

function* loadEntityAfterCheckUpdated({ loadActionCreator, successActionType }) {
  yield put(loadActionCreator());
  loadedEntitiesSuccessActinType.push(successActionType);
}
